import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SearchInput } from '@/components/shared/SearchInput';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Pagination } from '@/components/shared/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetMachines, useDeleteMachine } from '@/services/api/endpoints/MachineService';
import { useGetCompanies } from '@/services/api/endpoints/CompanyService';
import { useDebounce } from '@/hooks/useDebounce';
import { formatRelativeTime } from '@/lib/utils';
import { MachineModal } from './MachineModal';
import type { Machine } from '@/types';

export function MachinesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  // Filter state
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [connectionFilter, setConnectionFilter] = useState<string>('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState<Machine | null>(null);

  const { data, isLoading } = useGetMachines(
    page,
    20,
    debouncedSearch,
    'createdAt',
    'desc',
    statusFilter || undefined,
    connectionFilter || undefined,
    companyFilter || undefined
  );

  const { data: companiesData } = useGetCompanies(1, 100, '', 'name', 'asc');
  const deleteMachine = useDeleteMachine();

  const handleAddMachine = () => {
    setEditingMachine(null);
    setModalOpen(true);
  };

  const handleEditMachine = (e: React.MouseEvent, machine: Machine) => {
    e.stopPropagation();
    setEditingMachine(machine);
    setModalOpen(true);
  };

  const handleDeleteMachine = async (e: React.MouseEvent, machine: Machine) => {
    e.stopPropagation();
    const confirmed = window.confirm(t('machines.deleteConfirm'));
    if (!confirmed) return;

    try {
      await deleteMachine.mutateAsync(machine.id);
      toast.success(t('machines.deleteSuccess'));
    } catch {
      toast.error(t('machines.deleteError'));
    }
  };

  const handleRowClick = (machineId: string) => {
    navigate(`/machines/${machineId}`);
  };

  const handleCompanyFilterChange = (value: string) => {
    setCompanyFilter(value === 'all' ? '' : value);
    setPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value === 'all' ? '' : value);
    setPage(1);
  };

  const handleConnectionFilterChange = (value: string) => {
    setConnectionFilter(value === 'all' ? '' : value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('machines.title')}</h1>
        <Button className="gap-2" onClick={handleAddMachine}>
          <Plus className="h-4 w-4" />
          {t('machines.createMachine')}
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-sm">
          <SearchInput value={search} onChange={setSearch} />
        </div>

        {/* Company filter */}
        <Select value={companyFilter || 'all'} onValueChange={handleCompanyFilterChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('machines.company')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            {companiesData?.items.map((company) => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status filter */}
        <Select value={statusFilter || 'all'} onValueChange={handleStatusFilterChange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('common.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="active">{t('common.active')}</SelectItem>
            <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
            <SelectItem value="maintenance">{t('machines.maintenance')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Connection Status filter */}
        <Select value={connectionFilter || 'all'} onValueChange={handleConnectionFilterChange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('machines.connectionStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="online">{t('common.online')}</SelectItem>
            <SelectItem value="offline">{t('common.offline')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('machines.name')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('machines.code')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('machines.company')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('machines.type')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('machines.connectionStatus')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('machines.lastSeen')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('machines.productCount')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((machine) => (
                      <tr
                        key={machine.id}
                        className="border-b hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(machine.id)}
                      >
                        <td className="p-4">
                          <div className="font-medium">{machine.name}</div>
                          <div className="text-xs text-muted-foreground">{machine.deviceId}</div>
                        </td>
                        <td className="p-4">
                          <code className="text-sm bg-muted px-2 py-0.5 rounded">{machine.code}</code>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">{machine.company?.name || '-'}</td>
                        <td className="p-4 text-center">
                          {machine.type ? (
                            <Badge variant="outline" className="text-xs">
                              {machine.type === 'esp32' ? 'ESP32' : machine.type === 'pax_im30' ? 'PAX IM30' : machine.type === 'pax_im25' ? 'PAX IM25' : machine.type === 'ciontek_cm30' ? 'Ciontek CM30' : machine.type}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <StatusBadge status={machine.status} />
                        </td>
                        <td className="p-4 text-center">
                          <StatusBadge status={machine.connectionStatus} />
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {machine.lastSeenAt ? formatRelativeTime(machine.lastSeenAt) : '-'}
                        </td>
                        <td className="p-4 text-center text-sm">{machine.products?.length ?? '-'}</td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handleEditMachine(e, machine)}
                              title={t('common.edit')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handleDeleteMachine(e, machine)}
                              title={t('common.delete')}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data?.items.length === 0 && (
                      <tr>
                        <td colSpan={9} className="p-8 text-center text-muted-foreground">
                          {t('common.noResults')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {data?.items.map((machine) => (
                  <div
                    key={machine.id}
                    className="p-4 space-y-2 active:bg-muted/30 cursor-pointer"
                    onClick={() => handleRowClick(machine.id)}
                  >
                    <div>
                      <div className="font-medium">{machine.name}</div>
                      <div className="text-xs text-muted-foreground">{machine.deviceId}</div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('machines.code')}:</span>
                      <code className="text-sm bg-muted px-2 py-0.5 rounded">{machine.code}</code>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('machines.company')}:</span>
                      <span>{machine.company?.name || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {machine.type ? (
                        <Badge variant="outline" className="text-xs">
                          {machine.type === 'esp32' ? 'ESP32' : machine.type === 'pax_im30' ? 'PAX IM30' : machine.type === 'pax_im25' ? 'PAX IM25' : machine.type === 'ciontek_cm30' ? 'Ciontek CM30' : machine.type}
                        </Badge>
                      ) : null}
                      <StatusBadge status={machine.status} />
                      <StatusBadge status={machine.connectionStatus} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {machine.lastSeenAt ? formatRelativeTime(machine.lastSeenAt) : '-'}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleEditMachine(e, machine)}
                          title={t('common.edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleDeleteMachine(e, machine)}
                          title={t('common.delete')}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {data?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {data?.pagination && (
        <Pagination
          page={page}
          totalPages={data.pagination.totalPages}
          onPageChange={setPage}
        />
      )}

      <MachineModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        machine={editingMachine}
      />
    </div>
  );
}
